import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row, Col, Card, Table, Button, Input, Modal, Typography, Tag, Space, Select } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, ArrowLeftOutlined, TeamOutlined, SearchOutlined, ReloadOutlined, UserOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { confirm } = Modal;
const { Text } = Typography;
const { Option } = Select;

function FamilyMemberList() {
  const history  = useHistory();
  const location = useLocation();

  const familyId = new URLSearchParams(location.search).get('family_id');

  const [dataList, setDataList]       = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit]       = useState(20);
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(false);
  const [familyInfo, setFamilyInfo]   = useState(null);

  // Filters (standalone mode)
  const [filterGender, setFilterGender]       = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterMarried, setFilterMarried]     = useState('');
  const [filterVillages, setFilterVillages]   = useState([]);
  const [filterVillage, setFilterVillage]     = useState('');

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  useEffect(() => {
    if (familyId) {
      axios.get(`${baseUrl}/pop-families/${familyId}`, { headers: authHeader })
        .then(r => { if (r.data.status) setFamilyInfo(r.data.result); })
        .catch(() => {});
    } else {
      setFamilyInfo(null);
      // Load villages for standalone filter
      axios.get(`${baseUrl}/pop-village/list-all`, { headers: authHeader })
        .then(r => { if (r.data.status) setFilterVillages(r.data.result); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId]);

  const fetchList = (
    page      = currentPage,
    q         = search,
    gender    = filterGender,
    status    = filterStatus,
    married   = filterMarried,
    villageId = filterVillage,
  ) => {
    setLoading(true);
    const params = { page, limit, search: q };
    if (familyId) params.family_id = familyId;
    if (gender)   params.gender_type     = gender;
    if (status)   params.status          = status;
    if (married)  params.married_status  = married;
    if (villageId)params.village_id      = villageId;

    axios.get(`${baseUrl}/pop-family-member`, { headers: authHeader, params })
      .then(r => {
        if (r.data.status) {
          setDataList(r.data.result.map((item, i) => ({ ...item, key: (page - 1) * limit + i + 1 })));
          setTotalCount(r.data.totalCount);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { setCurrentPage(1); setSearch(''); fetchList(1, ''); }, [familyId]); // eslint-disable-line
  useEffect(() => { fetchList(); }, [currentPage]); // eslint-disable-line

  const handleReset = () => {
    setSearch(''); setFilterGender(''); setFilterStatus(''); setFilterMarried(''); setFilterVillage('');
    setCurrentPage(1);
    fetchList(1, '', '', '', '', '');
  };

  const handleDelete = (id) => confirm({
    title: 'Delete this member?', icon: <ExclamationCircleOutlined />,
    okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
    onOk: async () => {
      await axios.delete(`${baseUrl}/pop-family-member/${id}`, { headers: authHeader });
      fetchList();
    },
  });

  const columns = [
    { title: '#', dataIndex: 'key', key: 'key', width: 55 },
    { title: 'Member Name', dataIndex: 'member_name', key: 'member_name', width: 150 },
    ...(!familyId ? [{ title: 'Family (Head)', dataIndex: 'head_family', key: 'head_family', width: 150 }] : []),
    { title: 'Relation', dataIndex: 'relation_type', key: 'relation_type', width: 110 },
    { title: 'Gender', dataIndex: 'gender_type', key: 'gender_type', width: 80,
      render: v => v ? <Tag color={v === 'Male' ? 'blue' : 'pink'}>{v}</Tag> : '-' },
    { title: 'DOB', dataIndex: 'dob', key: 'dob', width: 100 },
    { title: 'Age', dataIndex: 'age', key: 'age', width: 60 },
    { title: 'Education', dataIndex: 'education_name', key: 'education_name', width: 130 },
    { title: 'Occupation', dataIndex: 'occupation_name', key: 'occupation_name', width: 130 },
    { title: 'Phone', dataIndex: 'phone_no', key: 'phone_no', width: 120 },
    { title: 'Married', dataIndex: 'married_status', key: 'married_status', width: 90 },
    { title: 'Village', dataIndex: 'village_name', key: 'village_name', width: 120 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={s === '1' ? 'green' : 'red'}>{s === '1' ? 'Active' : 'Inactive'}</Tag> },
    { title: 'Actions', key: 'actions', fixed: 'right', width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
            onClick={() => history.push(`/pop-family-member/update/${record.id}${familyId ? `?family_id=${familyId}` : ''}`)}>
            Edit
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const cardTitle = familyId ? (
    <Space align="center">
      <Button icon={<ArrowLeftOutlined />} size="small" onClick={() => history.push('/pop-families')}
        style={{ background: '#fff', borderColor: '#e2e8f0', color: '#7a0000', fontWeight: 600 }}>
        Families
      </Button>
      <div>
        <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>
          <UserOutlined style={{ marginRight: 6 }} />Family Members
        </span>
        {familyInfo && (
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 1 }}>
            {familyInfo.head_family}{familyInfo.head_father_name ? ` S/o ${familyInfo.head_father_name}` : ''}
            {familyInfo.village_name ? ` — ${familyInfo.village_name}` : ''}
          </div>
        )}
      </div>
    </Space>
  ) : (
    <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>
      <TeamOutlined style={{ marginRight: 6 }} />All Family Members
      <Tag color="blue" style={{ marginLeft: 8 }}>{totalCount}</Tag>
    </span>
  );

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            title={cardTitle}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Button icon={<PlusOutlined />}
                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600 }}
                onClick={() => history.push(`/pop-family-member/add${familyId ? `?family_id=${familyId}` : ''}`)}>
                Add Member
              </Button>
            }
          >
            {/* ── Filter Bar ── */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <Space wrap size={10}>
                <Input.Search
                  placeholder="Search by name or phone..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onSearch={val => { setSearch(val); setCurrentPage(1); fetchList(1, val, filterGender, filterStatus, filterMarried, filterVillage); }}
                  allowClear style={{ width: 240 }}
                  enterButton={<Button icon={<SearchOutlined />} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Search</Button>}
                />

                <Select placeholder="Gender" style={{ width: 120 }} allowClear
                  value={filterGender || undefined}
                  onChange={v => { setFilterGender(v || ''); setCurrentPage(1); fetchList(1, search, v || '', filterStatus, filterMarried, filterVillage); }}>
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                </Select>

                <Select placeholder="Married" style={{ width: 130 }} allowClear
                  value={filterMarried || undefined}
                  onChange={v => { setFilterMarried(v || ''); setCurrentPage(1); fetchList(1, search, filterGender, filterStatus, v || '', filterVillage); }}>
                  <Option value="Yes">Married</Option>
                  <Option value="No">Unmarried</Option>
                </Select>

                <Select placeholder="Status" style={{ width: 110 }} allowClear
                  value={filterStatus || undefined}
                  onChange={v => { setFilterStatus(v || ''); setCurrentPage(1); fetchList(1, search, filterGender, v || '', filterMarried, filterVillage); }}>
                  <Option value="1">Active</Option>
                  <Option value="0">Inactive</Option>
                </Select>

                {!familyId && (
                  <Select placeholder="Village" style={{ width: 180 }} allowClear showSearch optionFilterProp="children"
                    value={filterVillage || undefined}
                    onChange={v => { setFilterVillage(v || ''); setCurrentPage(1); fetchList(1, search, filterGender, filterStatus, filterMarried, v || ''); }}>
                    {filterVillages.map(v => <Option key={v.id} value={String(v.id)}>{v.name}</Option>)}
                  </Select>
                )}

                <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={dataList}
              loading={loading}
              size="small"
              scroll={{ x: 'max-content' }}
              pagination={{
                current: currentPage, pageSize: limit, total: totalCount,
                onChange: p => setCurrentPage(p),
                showTotal: t => `Total ${t} members`,
                showSizeChanger: false,
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default FamilyMemberList;
